import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import {
  groupLendwiseDocuments,
  groupEncompassAttachments,
  filterEmptyGroups
} from '@/services/extractionGroupingService';
import type { ExtractionDocumentGroup } from '@/types/extraction-grouping';

const log = createComponentLogger('useExtractionData', 'hooks');

export type ExtractionStatus = 'unprocessed' | 'pending' | 'extracting' | 'extracted' | 'classifying' | 'classified' | 'splitting' | 'completed' | 'failed';

export interface GPUMetrics {
  gpu_id: string;
  gpu_name: string;
  jobs_completed: number;
  total_pages: number;
  total_chars: number;
  avg_extraction_ms: number;
  avg_per_page_ms: number;
}

export interface DocumentWithExtraction {
  // Document info (from loan_documents)
  id: string;
  title: string | null;
  original_filename: string | null;
  file_size: number | null;
  source_file: string | null;
  document_created_at: string;

  // Extraction info (from document_processing_queue, may be null if unprocessed)
  queue_id: string | null;
  status: ExtractionStatus;
  progress: number;
  error_message: string | null;
  processed_by_gpu: string | null;
  extraction_time_ms: number | null;
  classification_time_ms: number | null;
  split_time_ms: number | null;
  total_time_ms: number | null;
  total_pages: number | null;
  extracted_char_count: number | null;
  extracted_text: string | null;
  classification_result: {
    type: string;
    confidence: number;
    page_ranges?: Array<{ start: number; end: number; type: string; confidence: number }>;
  } | null;
  queue_created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface ExtractionMetrics {
  total: number;
  unprocessed: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  avgExtractionTimeMs: number | null;
  avgClassificationTimeMs: number | null;
  avgTotalTimeMs: number | null;
  // GPU stats
  totalChars: number;
  totalPages: number;
  avgPerPageMs: number | null;
  gpuStats: GPUMetrics[];
}

export type FilterType = 'all' | 'unprocessed' | 'processing' | 'completed' | 'failed';

interface UseExtractionDataOptions {
  loanId?: string;
  limit?: number;
  pollInterval?: number;
}

export function useExtractionData(options: UseExtractionDataOptions = {}) {
  const { loanId, limit = 100, pollInterval = 5000 } = options;

  const [documents, setDocuments] = useState<DocumentWithExtraction[]>([]);
  const [metrics, setMetrics] = useState<ExtractionMetrics>({
    total: 0,
    unprocessed: 0,
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    avgExtractionTimeMs: null,
    avgClassificationTimeMs: null,
    avgTotalTimeMs: null,
    totalChars: 0,
    totalPages: 0,
    avgPerPageMs: null,
    gpuStats: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExtractionData = useCallback(async () => {
    if (!loanId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      // Fetch all documents for this loan with LEFT JOIN to queue
      const { data, error: fetchError } = await supabase
        .from('loan_documents')
        .select(`
          id,
          title,
          original_filename,
          file_size,
          source_file,
          created_at,
          document_processing_queue!left (
            id,
            status,
            progress,
            error_message,
            processed_by_gpu,
            extraction_time_ms,
            classification_time_ms,
            split_time_ms,
            total_pages,
            extracted_char_count,
            extracted_text,
            classification_result,
            created_at,
            started_at,
            completed_at
          )
        `)
        .eq('encompass_loan_id', loanId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (fetchError) {
throw fetchError;
}

      // Transform data to flat structure
      const transformedDocs: DocumentWithExtraction[] = (data || []).map((doc: Record<string, unknown>) => {
        // Queue data may be an array (due to LEFT JOIN) or null
        const queueData = doc.document_processing_queue as Record<string, unknown>[] | Record<string, unknown> | null;
        const queue = Array.isArray(queueData) ? queueData[0] : queueData;

        const extractionMs = queue ? (queue.extraction_time_ms as number) || 0 : 0;
        const classificationMs = queue ? (queue.classification_time_ms as number) || 0 : 0;
        const splitMs = queue ? (queue.split_time_ms as number) || 0 : 0;
        const totalTimeMs = extractionMs + classificationMs + splitMs;

        const queueStatus = queue?.status as string | undefined;
        const status: ExtractionStatus = queue ? (queueStatus as ExtractionStatus) : 'unprocessed';

        return {
          // Document info
          id: doc.id as string,
          title: doc.title as string | null,
          original_filename: doc.original_filename as string | null,
          file_size: doc.file_size as number | null,
          source_file: doc.source_file as string | null,
          document_created_at: doc.created_at as string,

          // Queue info
          queue_id: queue?.id as string | null ?? null,
          status,
          progress: (queue?.progress as number) ?? 0,
          error_message: (queue?.error_message as string | null) ?? null,
          processed_by_gpu: (queue?.processed_by_gpu as string | null) ?? null,
          extraction_time_ms: (queue?.extraction_time_ms as number | null) ?? null,
          classification_time_ms: (queue?.classification_time_ms as number | null) ?? null,
          split_time_ms: (queue?.split_time_ms as number | null) ?? null,
          total_time_ms: totalTimeMs > 0 ? totalTimeMs : null,
          total_pages: (queue?.total_pages as number | null) ?? null,
          extracted_char_count: (queue?.extracted_char_count as number | null) ?? null,
          extracted_text: (queue?.extracted_text as string | null) ?? null,
          classification_result: (queue?.classification_result as DocumentWithExtraction['classification_result']) ?? null,
          queue_created_at: (queue?.created_at as string | null) ?? null,
          started_at: (queue?.started_at as string | null) ?? null,
          completed_at: (queue?.completed_at as string | null) ?? null
        };
      });

      setDocuments(transformedDocs);

      // Calculate metrics
      const total = transformedDocs.length;
      const unprocessed = transformedDocs.filter((d) => d.status === 'unprocessed').length;
      const pending = transformedDocs.filter((d) => d.status === 'pending').length;
      const processing = transformedDocs.filter((d) =>
        ['extracting', 'extracted', 'classifying', 'classified', 'splitting'].includes(d.status)
      ).length;
      const completed = transformedDocs.filter((d) => d.status === 'completed').length;
      const failed = transformedDocs.filter((d) => d.status === 'failed').length;

      // Calculate averages from completed docs
      const completedDocs = transformedDocs.filter((d) => d.status === 'completed');
      const avgExtractionTimeMs =
        completedDocs.length > 0
          ? Math.round(
              completedDocs.reduce((sum, d) => sum + (d.extraction_time_ms || 0), 0) / completedDocs.length
            )
          : null;
      const avgClassificationTimeMs =
        completedDocs.length > 0
          ? Math.round(
              completedDocs.reduce((sum, d) => sum + (d.classification_time_ms || 0), 0) / completedDocs.length
            )
          : null;
      const avgTotalTimeMs =
        completedDocs.length > 0
          ? Math.round(
              completedDocs.reduce((sum, d) => sum + (d.total_time_ms || 0), 0) / completedDocs.length
            )
          : null;

      // Calculate GPU-specific stats
      const gpuMap = new Map<string, { jobs: number; pages: number; chars: number; extractionMs: number }>();
      completedDocs.forEach(doc => {
        const gpuId = doc.processed_by_gpu || 'unknown';
        const existing = gpuMap.get(gpuId) || { jobs: 0, pages: 0, chars: 0, extractionMs: 0 };
        gpuMap.set(gpuId, {
          jobs: existing.jobs + 1,
          pages: existing.pages + (doc.total_pages || 0),
          chars: existing.chars + (doc.extracted_char_count || 0),
          extractionMs: existing.extractionMs + (doc.extraction_time_ms || 0)
        });
      });

      const gpuStats: GPUMetrics[] = Array.from(gpuMap.entries()).map(([gpuId, stats]) => ({
        gpu_id: gpuId,
        gpu_name: gpuId === 'gpu1' ? 'RTX 5070' : gpuId === 'gpu2' ? 'RTX 3080 Ti' : 'Unknown',
        jobs_completed: stats.jobs,
        total_pages: stats.pages,
        total_chars: stats.chars,
        avg_extraction_ms: stats.jobs > 0 ? Math.round(stats.extractionMs / stats.jobs) : 0,
        avg_per_page_ms: stats.pages > 0 ? Math.round(stats.extractionMs / stats.pages) : 0
      })).sort((a, b) => b.jobs_completed - a.jobs_completed);

      const totalChars = completedDocs.reduce((sum, d) => sum + (d.extracted_char_count || 0), 0);
      const totalPages = completedDocs.reduce((sum, d) => sum + (d.total_pages || 0), 0);
      const totalExtractionMs = completedDocs.reduce((sum, d) => sum + (d.extraction_time_ms || 0), 0);
      const avgPerPageMs = totalPages > 0 ? Math.round(totalExtractionMs / totalPages) : null;

      setMetrics({
        total,
        unprocessed,
        pending,
        processing,
        completed,
        failed,
        avgExtractionTimeMs,
        avgClassificationTimeMs,
        avgTotalTimeMs,
        totalChars,
        totalPages,
        avgPerPageMs,
        gpuStats
      });

      setError(null);
    } catch (err) {
      log.error('Failed to fetch extraction data', {}, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch extraction data');
    } finally {
      setLoading(false);
    }
  }, [loanId, limit]);

  // Initial fetch
  useEffect(() => {
    fetchExtractionData();
  }, [fetchExtractionData]);

  // Polling for updates
  useEffect(() => {
    if (pollInterval <= 0) {
return;
}

    const interval = setInterval(fetchExtractionData, pollInterval);
    return () => clearInterval(interval);
  }, [fetchExtractionData, pollInterval]);

  // Real-time subscription for queue changes
  useEffect(() => {
    if (!loanId) {
return;
}

    const channel = supabase
      .channel('extraction_queue_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_processing_queue',
          filter: `encompass_loan_id=eq.${loanId}`
        },
        () => {
          // Refetch on any change
          fetchExtractionData();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [loanId, fetchExtractionData]);

  // Helper to filter documents
  const getFilteredDocuments = useCallback((filter: FilterType): DocumentWithExtraction[] => {
    switch (filter) {
      case 'unprocessed':
        return documents.filter((d) => d.status === 'unprocessed');
      case 'processing':
        return documents.filter((d) =>
          ['pending', 'extracting', 'extracted', 'classifying', 'classified', 'splitting'].includes(d.status)
        );
      case 'completed':
        return documents.filter((d) => d.status === 'completed');
      case 'failed':
        return documents.filter((d) => d.status === 'failed');
      default:
        return documents;
    }
  }, [documents]);

  // Group documents by classification category (mapped to Encompass groups)
  const groupedDocuments = useMemo((): ExtractionDocumentGroup<DocumentWithExtraction>[] => {
    return filterEmptyGroups(groupLendwiseDocuments(documents));
  }, [documents]);

  // Helper to get grouped documents with a filter applied first
  const getGroupedFilteredDocuments = useCallback((filter: FilterType): ExtractionDocumentGroup<DocumentWithExtraction>[] => {
    const filtered = getFilteredDocuments(filter);
    return filterEmptyGroups(groupLendwiseDocuments(filtered));
  }, [getFilteredDocuments]);

  return {
    documents,
    metrics,
    loading,
    error,
    refresh: fetchExtractionData,
    getFilteredDocuments,
    groupedDocuments,
    getGroupedFilteredDocuments
  };
}

// Legacy export for backwards compatibility
export type ExtractionJob = DocumentWithExtraction;

// ============================================================================
// Encompass Attachment Extraction Hook
// ============================================================================

export interface EncompassAttachmentWithExtraction {
  // Attachment info (from encompass_attachments)
  id: string;
  document_id: string;
  title: string | null;
  file_name: string | null;
  file_size: number | null;
  storage_path: string | null;
  storage_bucket: string | null;
  download_status: 'pending' | 'downloading' | 'downloaded' | 'failed' | 'unavailable' | 'deleted';
  attachment_created_at: string;

  // Extraction info (from document_processing_queue, may be null if unprocessed)
  queue_id: string | null;
  status: ExtractionStatus;
  progress: number;
  error_message: string | null;
  processed_by_gpu: string | null;
  extraction_time_ms: number | null;
  classification_time_ms: number | null;
  split_time_ms: number | null;
  total_time_ms: number | null;
  total_pages: number | null;
  extracted_char_count: number | null;
  extracted_text: string | null;
  classification_result: {
    type: string;
    confidence: number;
    page_ranges?: Array<{ start: number; end: number; type: string; confidence: number }>;
  } | null;
  queue_created_at: string | null;
  started_at: string | null;
  completed_at: string | null;

  // Can only process if downloaded
  canProcess: boolean;
}

interface UseEncompassExtractionDataOptions {
  loanId?: string;
  limit?: number;
  pollInterval?: number;
}

export function useEncompassExtractionData(options: UseEncompassExtractionDataOptions = {}) {
  const { loanId, limit = 500, pollInterval = 5000 } = options;

  const [attachments, setAttachments] = useState<EncompassAttachmentWithExtraction[]>([]);
  const [documentGroupsMap, setDocumentGroupsMap] = useState<Map<string, string[]>>(new Map());
  const [metrics, setMetrics] = useState<ExtractionMetrics>({
    total: 0,
    unprocessed: 0,
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    avgExtractionTimeMs: null,
    avgClassificationTimeMs: null,
    avgTotalTimeMs: null,
    totalChars: 0,
    totalPages: 0,
    avgPerPageMs: null,
    gpuStats: []
  });
  const [downloadStats, setDownloadStats] = useState({
    downloaded: 0,
    pending: 0,
    failed: 0,
    unavailable: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEncompassExtractionData = useCallback(async () => {
    if (!loanId) {
      setAttachments([]);
      setLoading(false);
      return;
    }

    try {
      // Query attachments directly by encompass_loan_id (not through documents)
      // This ensures we catch all attachments, including orphaned ones without encompass_document_id
      const { data: attachmentData, error: attachError } = await supabase
        .from('encompass_attachments')
        .select('id, encompass_document_id, title, file_name, file_type, file_size, storage_path, storage_bucket, download_status, created_at')
        .eq('encompass_loan_id', loanId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (attachError) {
throw attachError;
}

      // Get unique document IDs to fetch their groups
      const documentIds = [...new Set((attachmentData || []).map(a => a.encompass_document_id).filter(Boolean))];
      const docGroupsMap = new Map<string, string[]>();

      if (documentIds.length > 0) {
        const { data: docsData, error: docsError } = await supabase
          .from('encompass_documents')
          .select('id, document_groups')
          .in('id', documentIds);

        if (docsError) {
          log.warn('Failed to fetch document groups', { error: docsError.message });
        } else if (docsData) {
          for (const doc of docsData) {
            if (doc.document_groups && Array.isArray(doc.document_groups)) {
              docGroupsMap.set(doc.id, doc.document_groups);
            }
          }
        }
      }
      setDocumentGroupsMap(docGroupsMap);

      // Get queue data for these attachments
      const attachmentIds = (attachmentData || []).map(a => a.id);
      const queueMap = new Map<string, Record<string, unknown>>();

      if (attachmentIds.length > 0) {
        const { data: queueData, error: queueError } = await supabase
          .from('document_processing_queue')
          .select(`
            id,
            encompass_attachment_id,
            status,
            progress,
            error_message,
            processed_by_gpu,
            extraction_time_ms,
            classification_time_ms,
            split_time_ms,
            total_pages,
            extracted_char_count,
            extracted_text,
            classification_result,
            created_at,
            started_at,
            completed_at
          `)
          .in('encompass_attachment_id', attachmentIds);

        if (queueError) {
          log.warn('Failed to fetch queue data for Encompass attachments', { error: queueError.message });
        } else if (queueData) {
          for (const q of queueData) {
            if (q.encompass_attachment_id) {
              queueMap.set(q.encompass_attachment_id, q);
            }
          }
        }
      }

      // Transform data
      const transformed: EncompassAttachmentWithExtraction[] = (attachmentData || []).map((att) => {
        const queue = queueMap.get(att.id);

        const extractionMs = queue ? (queue.extraction_time_ms as number) || 0 : 0;
        const classificationMs = queue ? (queue.classification_time_ms as number) || 0 : 0;
        const splitMs = queue ? (queue.split_time_ms as number) || 0 : 0;
        const totalTimeMs = extractionMs + classificationMs + splitMs;

        const queueStatus = queue?.status as string | undefined;
        const status: ExtractionStatus = queue ? (queueStatus as ExtractionStatus) : 'unprocessed';
        const canProcess = att.download_status === 'downloaded' && !!att.storage_path;

        return {
          id: att.id,
          document_id: att.encompass_document_id,
          title: att.title,
          file_name: att.file_name || att.title,
          file_size: att.file_size,
          storage_path: att.storage_path,
          storage_bucket: att.storage_bucket,
          download_status: att.download_status,
          attachment_created_at: att.created_at,

          queue_id: queue?.id as string | null ?? null,
          status,
          progress: (queue?.progress as number) ?? 0,
          error_message: (queue?.error_message as string | null) ?? null,
          processed_by_gpu: (queue?.processed_by_gpu as string | null) ?? null,
          extraction_time_ms: (queue?.extraction_time_ms as number | null) ?? null,
          classification_time_ms: (queue?.classification_time_ms as number | null) ?? null,
          split_time_ms: (queue?.split_time_ms as number | null) ?? null,
          total_time_ms: totalTimeMs > 0 ? totalTimeMs : null,
          total_pages: (queue?.total_pages as number | null) ?? null,
          extracted_char_count: (queue?.extracted_char_count as number | null) ?? null,
          extracted_text: (queue?.extracted_text as string | null) ?? null,
          classification_result: (queue?.classification_result as EncompassAttachmentWithExtraction['classification_result']) ?? null,
          queue_created_at: (queue?.created_at as string | null) ?? null,
          started_at: (queue?.started_at as string | null) ?? null,
          completed_at: (queue?.completed_at as string | null) ?? null,
          canProcess
        };
      });

      setAttachments(transformed);

      // Calculate download stats
      const downloaded = transformed.filter(a => a.download_status === 'downloaded').length;
      const pendingDownload = transformed.filter(a => a.download_status === 'pending' || a.download_status === 'downloading').length;
      const failedDownload = transformed.filter(a => a.download_status === 'failed').length;
      const unavailable = transformed.filter(a => a.download_status === 'unavailable').length;
      setDownloadStats({ downloaded, pending: pendingDownload, failed: failedDownload, unavailable });

      // Calculate extraction metrics (only for downloaded attachments)
      const downloadedAttachments = transformed.filter(a => a.download_status === 'downloaded');
      const total = downloadedAttachments.length;
      const unprocessed = downloadedAttachments.filter(d => d.status === 'unprocessed').length;
      const pendingQueue = downloadedAttachments.filter(d => d.status === 'pending').length;
      const processing = downloadedAttachments.filter(d =>
        ['extracting', 'extracted', 'classifying', 'classified', 'splitting'].includes(d.status)
      ).length;
      const completed = downloadedAttachments.filter(d => d.status === 'completed').length;
      const failed = downloadedAttachments.filter(d => d.status === 'failed').length;

      const completedDocs = downloadedAttachments.filter(d => d.status === 'completed');
      const avgExtractionTimeMs = completedDocs.length > 0
        ? Math.round(completedDocs.reduce((sum, d) => sum + (d.extraction_time_ms || 0), 0) / completedDocs.length)
        : null;
      const avgClassificationTimeMs = completedDocs.length > 0
        ? Math.round(completedDocs.reduce((sum, d) => sum + (d.classification_time_ms || 0), 0) / completedDocs.length)
        : null;
      const avgTotalTimeMs = completedDocs.length > 0
        ? Math.round(completedDocs.reduce((sum, d) => sum + (d.total_time_ms || 0), 0) / completedDocs.length)
        : null;

      // Calculate GPU-specific stats
      const gpuMap = new Map<string, { jobs: number; pages: number; chars: number; extractionMs: number }>();
      completedDocs.forEach(doc => {
        const gpuId = doc.processed_by_gpu || 'unknown';
        const existing = gpuMap.get(gpuId) || { jobs: 0, pages: 0, chars: 0, extractionMs: 0 };
        gpuMap.set(gpuId, {
          jobs: existing.jobs + 1,
          pages: existing.pages + (doc.total_pages || 0),
          chars: existing.chars + (doc.extracted_char_count || 0),
          extractionMs: existing.extractionMs + (doc.extraction_time_ms || 0)
        });
      });

      const gpuStats: GPUMetrics[] = Array.from(gpuMap.entries()).map(([gpuId, stats]) => ({
        gpu_id: gpuId,
        gpu_name: gpuId === 'gpu1' ? 'RTX 5070' : gpuId === 'gpu2' ? 'RTX 3080 Ti' : 'Unknown',
        jobs_completed: stats.jobs,
        total_pages: stats.pages,
        total_chars: stats.chars,
        avg_extraction_ms: stats.jobs > 0 ? Math.round(stats.extractionMs / stats.jobs) : 0,
        avg_per_page_ms: stats.pages > 0 ? Math.round(stats.extractionMs / stats.pages) : 0
      })).sort((a, b) => b.jobs_completed - a.jobs_completed);

      const totalChars = completedDocs.reduce((sum, d) => sum + (d.extracted_char_count || 0), 0);
      const totalPages = completedDocs.reduce((sum, d) => sum + (d.total_pages || 0), 0);
      const totalExtractionMs = completedDocs.reduce((sum, d) => sum + (d.extraction_time_ms || 0), 0);
      const avgPerPageMs = totalPages > 0 ? Math.round(totalExtractionMs / totalPages) : null;

      setMetrics({
        total,
        unprocessed,
        pending: pendingQueue,
        processing,
        completed,
        failed,
        avgExtractionTimeMs,
        avgClassificationTimeMs,
        avgTotalTimeMs,
        totalChars,
        totalPages,
        avgPerPageMs,
        gpuStats
      });

      setError(null);
    } catch (err) {
      log.error('Failed to fetch Encompass extraction data', {}, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to fetch Encompass extraction data');
    } finally {
      setLoading(false);
    }
  }, [loanId, limit]);

  // Initial fetch
  useEffect(() => {
    fetchEncompassExtractionData();
  }, [fetchEncompassExtractionData]);

  // Polling for updates
  useEffect(() => {
    if (pollInterval <= 0) {
return;
}

    const interval = setInterval(fetchEncompassExtractionData, pollInterval);
    return () => clearInterval(interval);
  }, [fetchEncompassExtractionData, pollInterval]);

  // Real-time subscription for queue changes
  useEffect(() => {
    if (!loanId) {
return;
}

    const channel = supabase
      .channel('encompass_extraction_queue_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_processing_queue',
          filter: `encompass_loan_id=eq.${loanId}`
        },
        () => {
          fetchEncompassExtractionData();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [loanId, fetchEncompassExtractionData]);

  // Helper to filter attachments
  const getFilteredAttachments = useCallback((filter: FilterType): EncompassAttachmentWithExtraction[] => {
    // Only include downloaded attachments in filter views
    const downloadedOnly = attachments.filter(a => a.download_status === 'downloaded');

    switch (filter) {
      case 'unprocessed':
        return downloadedOnly.filter(d => d.status === 'unprocessed');
      case 'processing':
        return downloadedOnly.filter(d =>
          ['pending', 'extracting', 'extracted', 'classifying', 'classified', 'splitting'].includes(d.status)
        );
      case 'completed':
        return downloadedOnly.filter(d => d.status === 'completed');
      case 'failed':
        return downloadedOnly.filter(d => d.status === 'failed');
      default:
        return downloadedOnly;
    }
  }, [attachments]);

  // Group attachments by parent document's document_groups
  const groupedAttachments = useMemo((): ExtractionDocumentGroup<EncompassAttachmentWithExtraction>[] => {
    // Only include downloaded attachments
    const downloadedOnly = attachments.filter(a => a.download_status === 'downloaded');
    return filterEmptyGroups(groupEncompassAttachments(downloadedOnly, documentGroupsMap));
  }, [attachments, documentGroupsMap]);

  // Helper to get grouped attachments with a filter applied first
  const getGroupedFilteredAttachments = useCallback((filter: FilterType): ExtractionDocumentGroup<EncompassAttachmentWithExtraction>[] => {
    const filtered = getFilteredAttachments(filter);
    return filterEmptyGroups(groupEncompassAttachments(filtered, documentGroupsMap));
  }, [getFilteredAttachments, documentGroupsMap]);

  return {
    attachments,
    metrics,
    downloadStats,
    loading,
    error,
    refresh: fetchEncompassExtractionData,
    getFilteredAttachments,
    groupedAttachments,
    getGroupedFilteredAttachments,
    documentGroupsMap
  };
}
